import { useTutorial } from '../../contexts/TutorialContext';
import { Spotlight } from './Spotlight';
import { TutorialTooltip } from './TutorialTooltip';
import { TutorialProgress } from './TutorialProgress';
import './TutorialOverlay.css';

/**
 * TutorialOverlay is the main container component that renders:
 * - Dark overlay with spotlight cutout
 * - Tutorial tooltip with current step
 * - Progress bar at top
 * 
 * It reads state from TutorialContext and renders nothing if no tutorial is active.
 */
export function TutorialOverlay() {
  const {
    state,
    currentStep,
    currentTutorial,
    totalSteps,
    progress,
    nextStep,
    prevStep,
    skipStep,
    exitTutorial,
    fillPrompt,
  } = useTutorial();
  
  // Don't render if no tutorial is active
  if (!state.isActive || !currentStep || !currentTutorial) {
    return null;
  }
  
  // Determine button visibility
  const showNextButton = currentStep.showNextButton !== false && currentStep.type !== 'action';
  const showPrevButton = false; // Hidden for now
  const showSkipButton = false; // Hidden for now
  
  // Special handling for celebration type
  const isCelebration = currentStep.type === 'celebration';
  
  return (
    <div className={`tutorial-overlay-container ${isCelebration ? 'tutorial-overlay--celebration' : ''}`}>
      {/* Progress bar at top */}
      <TutorialProgress
        currentStep={state.currentStepIndex + 1}
        totalSteps={totalSteps}
        progress={progress}
        tutorialName={currentTutorial.name}
        onExit={exitTutorial}
      />
      
      {/* Spotlight overlay — hidden on celebration so the full canvas is visible */}
      {!isCelebration && (
        <Spotlight 
          target={currentStep.target}
          onClickOutside={showSkipButton ? skipStep : undefined}
        />
      )}
      
      {/* Tooltip */}
      <TutorialTooltip
        target={currentStep.target}
        position={currentStep.position}
        title={currentStep.title}
        content={currentStep.content}
        copyText={currentStep.copyText}
        onUsePrompt={fillPrompt}
        showNextButton={showNextButton}
        showPrevButton={showPrevButton}
        showSkipButton={showSkipButton}
        stepNumber={state.currentStepIndex + 1}
        totalSteps={totalSteps}
        onNext={nextStep}
        onPrev={prevStep}
        onSkip={skipStep}
        onExit={exitTutorial}
      />
      
      {/* Celebration confetti effect */}
      {isCelebration && <CelebrationEffect />}
    </div>
  );
}

/**
 * Simple confetti-like celebration effect
 */
function CelebrationEffect() {
  return (
    <div className="celebration-container">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="confetti"
          style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 0.5}s`,
            backgroundColor: ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'][
              Math.floor(Math.random() * 5)
            ],
          }}
        />
      ))}
    </div>
  );
}
